import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ColumnTypes } from '@features/navigation/desktop/main-menu/main-menu-config';
import { mainMenuFade } from '@features/navigation/desktop/main-menu/main-menu.animation';
import { MainMenuLevel1ViewModel } from '@models/api.models';
import { GtmService } from '@core/services/gtm/gtm.service';
import { SideAlignmentPipe } from '@features/navigation/desktop/main-menu/side-alignment.pipe';
import { MainMenuGroupComponent } from '@features/navigation/desktop/main-menu-group/main-menu-group.component';
import { NgCheckedDirective } from '@shared/ng-checked/ng-checked.directive';
import { LinkComponent } from '@shared/routing/link/link.component';
import { NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgTemplateOutlet } from '@angular/common';

@Component({
    selector: 'app-main-menu',
    templateUrl: './main-menu.component.html',
    styleUrls: ['./main-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [mainMenuFade],
    standalone: true,
    imports: [
        NgClass,
        NgIf,
        NgTemplateOutlet,
        NgFor,
        LinkComponent,
        NgSwitch,
        NgSwitchCase,
        NgCheckedDirective,
        MainMenuGroupComponent,
        SideAlignmentPipe
    ]
})
export class MainMenuComponent implements OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private timer = 0;

    public maxHeight: number = 0;
    public activeGroupIndex: number | null = null;
    public columnTypes = ColumnTypes;

    @ViewChild('dropdownBackground')
    private readonly dropdownBackground: ElementRef<HTMLDivElement>;

    @ViewChildren('dropdown')
    private readonly dropdowns: QueryList<ElementRef<HTMLDivElement>>;

    @Input()
    public readonly menuGroups?: MainMenuLevel1ViewModel[];

    @Output()
    public readonly updateActiveIndexEvent: EventEmitter<number | null> = new EventEmitter();

    constructor(
        private readonly ngZone: NgZone,
        private readonly router: Router,
        private readonly gtmService: GtmService
    ) {
        this.router.events
            .pipe(
                takeUntil(this.unsubscribe),
                map(e => {
                    if (this.activeGroupIndex !== null) {
                        return e;
                    }
                }),
                filter(e => e instanceof NavigationEnd)
            )
            .subscribe(() => {
                if (this.activeGroupIndex) {
                    this.gtmService.trackMenuNavigation(this.router.url, undefined, undefined, true);
                }

                this.resetDropdown();
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    // Set max dropdown height on mouseover
    public setDropdown(
        e: MouseEvent | Event,
        hasChildren: boolean | undefined,
        index: number,
        delay: number = 0
    ): void {
        // If hovering a link without children, we reset state
        if (!hasChildren) {
            this.resetDropdown();
            return;
        }

        const distance = (e.target as HTMLElement)?.getBoundingClientRect().bottom;
        const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        this.maxHeight = vh - distance;
        clearTimeout(this.timer);

        this.ngZone.runOutsideAngular(() => {
            this.timer = window.setTimeout(() => {
                this.ngZone.run(() => {
                    this.activeGroupIndex = index;
                    this.updateActiveIndexEvent.emit(this.activeGroupIndex);
                });
            }, delay);
        });
    }

    public resetDropdown(): void {
        this.activeGroupIndex = null;
        this.updateActiveIndexEvent.emit(null);
        clearTimeout(this.timer);

        this.ngZone.runOutsideAngular(() => {
            if (this.dropdownBackground.nativeElement) {
                this.dropdownBackground.nativeElement.style.transform = 'translateY(-100%)';
            }
        });
    }

    // Dynamically set height when new content is rendered
    public setHeight(dropdownEl: HTMLDivElement): void {
        if (!this.dropdownBackground.nativeElement || !dropdownEl?.offsetHeight) {
            return;
        }

        // We eject from angular since we do not want to trigger change detection
        // every time we want to animate the background
        this.ngZone.runOutsideAngular(() => {
            // Calculate percentage of dropdown content
            const contentHeight = (100 * dropdownEl.offsetHeight) / this.maxHeight;
            // If more than screen real estate, just take up remaining
            const percentageTravel = contentHeight > 100 ? 0 : contentHeight - 100;
            this.dropdownBackground.nativeElement.style.transform = `translateY(${percentageTravel}%)`;
        });
    }
}
